import React from 'react';
import { Button, Menu, MenuItem, MenuTrigger } from '@chatsdet/ui';
import { ArrowClockwise, DotsThreeVertical, Play, Prohibit } from '@phosphor-icons/react';
import type { RunAreaDto } from '../../../features/external-tests/useExternalRun';

/**
 * Row three-dot menu for {@link ExternalRunAreas}, matching the pattern in
 * `TestRunActionsMenu`: a ghost icon-only trigger that stops row-click
 * propagation, with a `Menu`/`MenuItem` popover for the available actions.
 */
export function ExternalRunAreaActionsMenu({
  area,
  isRunFinished,
  isRerunPending,
  onRerunArea,
  runnerDisabledAreas,
  isSetAreaDisabledPending,
  onToggleAreaDisabled,
  suiteId,
}: {
  area: RunAreaDto;
  isRunFinished: boolean;
  isRerunPending: boolean;
  onRerunArea: (areaIndex: number) => void;
  runnerDisabledAreas: string[];
  isSetAreaDisabledPending: boolean;
  onToggleAreaDisabled: (area: RunAreaDto, disabled: boolean) => void;
  suiteId: string | null;
}) {
  // Only a directory area can be disabled — a synthetic single-shard
  // "All tests" area (`specPath === null`) has nothing to exclude.
  const isDisabled = area.specPath != null && runnerDisabledAreas.includes(area.specPath);
  const canToggle = area.specPath != null && suiteId != null;
  // Hidden once the area is off — in this run's snapshot or turned off since —
  // because the server refuses that re-run.
  const canRerun = isRunFinished && area.state !== 'disabled' && !isDisabled;

  if (!canRerun && !canToggle) return null;

  return (
    <div className="flex justify-end" onClick={(event) => event.stopPropagation()}>
      <MenuTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="p-2 text-muted-foreground"
          aria-label={`Actions for ${area.label}`}
        >
          <DotsThreeVertical size={16} weight="bold" />
        </Button>
        <Menu
          onAction={(key) => {
            if (key === 'rerun') onRerunArea(area.index);
            if (key === 'toggle-disabled') onToggleAreaDisabled(area, !isDisabled);
          }}
        >
          {canRerun && (
            <MenuItem id="rerun" isDisabled={isRerunPending}>
              <ArrowClockwise size={14} className="mr-2" />
              Re-run area
            </MenuItem>
          )}
          {canToggle && (
            <MenuItem id="toggle-disabled" isDisabled={isSetAreaDisabledPending}>
              {isDisabled ? (
                <Play size={14} className="mr-2" />
              ) : (
                <Prohibit size={14} className="mr-2" />
              )}
              {isDisabled ? 'Enable area' : 'Disable area'}
            </MenuItem>
          )}
        </Menu>
      </MenuTrigger>
    </div>
  );
}
